/**
 * Access control helpers.
 *
 * The backend owns access control: GET /auth/me returns `has_all_access` and
 * `accessible_customer_ids`, and the customer, site and vehicle list endpoints
 * are scoped to the caller. These helpers re-apply the same scope client-side
 * so pickers never show, or query for, entities outside the user's access.
 */

import { UI_STRINGS } from '@/data/constants/strings'

export const ACCESS_CONTROL_CONFIG = {
  noAccessErrorMessage: UI_STRINGS.NO_ACCESS_ERROR,
}

/**
 * @param {Object} userProfile - Profile from GET /auth/me
 * @returns {Array|'ALL'} Customer IDs the user may access, or 'ALL' for admins
 */
export function getAccessibleCustomerIds(userProfile) {
  if (!userProfile) return []
  if (userProfile.has_all_access === true) return 'ALL'
  return Array.isArray(userProfile.accessible_customer_ids)
    ? userProfile.accessible_customer_ids
    : []
}

export function hasCustomerAccess(userProfile, customerId) {
  if (!userProfile || !customerId) return false
  const accessible = getAccessibleCustomerIds(userProfile)
  return accessible === 'ALL' || accessible.includes(customerId)
}

// is_active is the backend's 60-day activity flag; absent means active
export const isEntityActive = (entity) => entity?.is_active !== false
const isActive = isEntityActive

// Vehicles carry no such flag, so the same window is applied to their last
// uploaded video (from the live-status poll). A vehicle with no status is
// treated as active: it may be newly commissioned, or the poll may still be loading.
export const INACTIVE_AFTER_DAYS = 60
const INACTIVE_AFTER_MINUTES = INACTIVE_AFTER_DAYS * 24 * 60

export const isVehicleInactive = (liveStatus) =>
  (liveStatus?.minutes_since_last_segment ?? 0) > INACTIVE_AFTER_MINUTES

export function filterAccessibleCustomers(customers, userProfile, hideInactive = true) {
  if (!Array.isArray(customers)) return []
  return customers.filter(
    (customer) =>
      hasCustomerAccess(userProfile, customer.id) && (!hideInactive || isActive(customer))
  )
}

export function filterAccessibleSites(sites, userProfile, hideInactive = true) {
  if (!Array.isArray(sites)) return []
  return sites.filter(
    (site) => hasCustomerAccess(userProfile, site.customer_id) && (!hideInactive || isActive(site))
  )
}

/**
 * Vehicles are scoped through their customer. Older backends list vehicles
 * without a customer_id; when the whole list lacks one, scope by the site's
 * customer instead.
 */
export function filterAccessibleVehicles(
  vehicles,
  userProfile,
  hideInactive = true,
  siteCustomerId = null
) {
  if (!Array.isArray(vehicles)) return []

  const noVehicleHasCustomer = vehicles.every(
    (v) => v.customer_id == null || v.customer_id === 'null'
  )
  const inScope =
    siteCustomerId && noVehicleHasCustomer
      ? () => hasCustomerAccess(userProfile, siteCustomerId)
      : (vehicle) => hasCustomerAccess(userProfile, vehicle.customer_id)

  return vehicles.filter(
    (vehicle) =>
      inScope(vehicle) && (!hideInactive || (isActive(vehicle.customer) && isActive(vehicle.site)))
  )
}
